import type { S2SConsentSnapshot } from "../socket_handler/data_structures/to_server/custom_event_request.js";

export const APPLOVIN_CONSENT_POLICY_VERSION = 'applovin-android-tcf-v2';

export type AppLovinInitializationResult = {
  countryCode?: unknown;
  isConsentRequired?: unknown;
  consentFlowStatus?: unknown;
  userConsent?: unknown;
  gdprApplies?: unknown;
  purposeConsents?: unknown;
  purposeLegitimateInterests?: unknown;
  error?: unknown;
};

export type AppsFlyerConsentDecision = {
  startAllowed: boolean;
  advertisingIdAllowed: boolean;
};

export type CasConsentDecision = {
  snapshot: S2SConsentSnapshot;
  appsFlyer: AppsFlyerConsentDecision;
  reason: 'accepted' | 'denied' | 'not_required' | 'error_or_unknown';
};

type ConsentDecisionDependencies = {
  now?: () => Date;
  makeId?: () => string;
};

function normalizeStatus( value: unknown ): string
{
  return String( value ?? '' )
    .trim()
    .toLowerCase()
    .replace( /[\s_-]+/g, '' );
}

function makeConsentId(): string
{
  const uuid = globalThis.crypto?.randomUUID?.();
  if ( uuid ) return uuid;

  const random = Math.random().toString( 16 ).slice( 2 );
  return `applovin-${Date.now().toString( 16 )}-${random}`;
}

function resolveRegion( result: AppLovinInitializationResult ): string
{
  const country_code = String( result.countryCode ?? '' ).trim().toUpperCase();
  if ( isGdprCountry( country_code ) ) return 'eea';
  if ( /^[A-Z]{2}$/.test( country_code ) ) return `country_${country_code}`;
  if ( result.isConsentRequired === true ) return 'regulated';
  if ( result.isConsentRequired === false ) return 'non_regulated';
  return 'unknown';
}

const GDPR_COUNTRY_CODES = new Set( [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR',
  'GR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU', 'LV', 'MT', 'NL', 'NO',
  'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'CH',
] );

function isGdprCountry( countryCode: string ): boolean
{
  return GDPR_COUNTRY_CODES.has( countryCode );
}

function parseOptionalBoolean( value: unknown ): boolean | null
{
  if ( value === true || value === 1 || value === '1' ) return true;
  if ( value === false || value === 0 || value === '0' ) return false;
  if ( typeof value === 'string' )
  {
    const normalized = value.trim().toLowerCase();
    if ( normalized === 'true' ) return true;
    if ( normalized === 'false' ) return false;
  }
  return null;
}

function hasPurpose( bitString: unknown, purposeId: number ): boolean
{
  if ( typeof bitString !== 'string' || purposeId < 1 ) return false;
  return bitString.charAt( purposeId - 1 ) === '1';
}

/**
 * Converts the AppLovin MAX consent flow (Google UMP) result into the occurrence-time privacy snapshot used by
 * S2S. First-party analytics (Athena) and AppsFlyer run regardless of the decision; AppsFlyer receives the
 * decision itself through its TCF data collection. The snapshot gates ad identifiers and Meta/Google S2S.
 *
 * Snapshot policy:
 * - in a GDPR region, an explicit consent accept is narrowed by the TCF purpose
 *   grants persisted by the CMP (P1 + P7 for analytics/measurement, P3 + P4
 *   for personalization);
 * - an explicit accept outside a GDPR region grants all three categories;
 * - an explicit deny, a flow error, or an unknown state grants nothing;
 * - NOT_REQUIRED permits analytics and measurement in the non-regulated
 *   region, while personalization remains disabled without an explicit accept.
 */
export function mapAppLovinConsentDecision(
  result: AppLovinInitializationResult,
  dependencies: ConsentDecisionDependencies = {},
): CasConsentDecision
{
  const flow_status = normalizeStatus( result.consentFlowStatus );
  const user_consent = normalizeStatus( result.userConsent );
  const has_error = String( result.error ?? '' ).trim().length > 0
    || new Set( [
      'unavailable',
      'internalerror',
      'networkerror',
      'invalidcontext',
      'contextinvalid',
      'stillpresenting',
      'flowstillshowing',
      'timeout',
    ] ).has( flow_status );

  let reason: CasConsentDecision['reason'] = 'error_or_unknown';
  let analytics_allowed = false;
  let ad_measurement_allowed = false;
  let ad_personalization_allowed = false;
  const country_code = String( result.countryCode ?? '' ).trim().toUpperCase();
  const gdpr_applies = parseOptionalBoolean( result.gdprApplies );
  const is_gdpr_region = gdpr_applies === true
    || isGdprCountry( country_code )
    || ( gdpr_applies == null && result.isConsentRequired === true );

  if ( !has_error && user_consent === 'denied' )
  {
    reason = 'denied';
  }
  else if (
    !has_error
    && !is_gdpr_region
    && result.isConsentRequired === false
    && flow_status === 'notrequired'
  )
  {
    reason = 'not_required';
    analytics_allowed = true;
    ad_measurement_allowed = true;
  }
  else if ( !has_error && user_consent === 'accepted' )
  {
    reason = 'accepted';
    if ( is_gdpr_region )
    {
      const purpose_1 = hasPurpose( result.purposeConsents, 1 );
      const purpose_7 = hasPurpose( result.purposeConsents, 7 )
        || hasPurpose( result.purposeLegitimateInterests, 7 );
      analytics_allowed = purpose_1 && purpose_7;
      ad_measurement_allowed = analytics_allowed;
      ad_personalization_allowed = analytics_allowed
        && hasPurpose( result.purposeConsents, 3 )
        && hasPurpose( result.purposeConsents, 4 );
    }
    else
    {
      analytics_allowed = true;
      ad_measurement_allowed = true;
      ad_personalization_allowed = true;
    }
  }

  const now = dependencies.now?.() ?? new Date();
  const id = dependencies.makeId?.() ?? makeConsentId();
  const snapshot: S2SConsentSnapshot = {
    id,
    region: resolveRegion( result ),
    att_status: 'not_applicable',
    analytics_allowed,
    ad_measurement_allowed,
    ad_personalization_allowed,
    timestamp: now.toISOString(),
    policy_version: APPLOVIN_CONSENT_POLICY_VERSION,
  };

  return {
    snapshot,
    appsFlyer: {
      startAllowed: true,
      advertisingIdAllowed: ad_measurement_allowed || ( !is_gdpr_region && reason !== 'denied' ),
    },
    reason,
  };
}
